import { Button } from '@medly-components/core';
import { TourDialogBox, DialogBoxFooter, TourContent } from './MapTourDialogBox.styled';
import { useCallback, useContext } from 'react';
import { MAP_SPECIFIC_TOUR_START_INDEX, tourSteps } from './TourSteps';
import { DONE, NEXT, PREVIOUS } from '../../../../constants/common';
import { useMapTour } from '../../hooks/useMapTour/useMapTour';
import { MapTourContext } from 'context/MapTourContext';
import { CloseButton, DialogBoxContent, TourHeader, TourStepper } from 'components/common/GuidedTour/GuidedTour.styled';
import { DialogBox } from 'components/common/DialogBox/DialogBox';
import { useBoolean } from 'hooks';
import { CONTINUE_EXPLORING, EXIT, YOU_ARE_ALL_SET, YOU_WANT_TO_EXIT } from 'constants/text/common';
import { RESUME_TOUR, RESUME_TOUR_DESCRIPTION, TOUR_FINISH_DESCRIPTION } from 'constants/text/mapTour';
import { TourStep } from './types';

export const MapTourDialogBox = () => {
    const { tour, setIsMapTour, setTour } = useContext(MapTourContext);
    const { updateTour, finishTour, isTourFinished } = useMapTour();
    const { value: isResumeDailogOpen, setTrue: showResumeDailog, setFalse: hideResumeDialog } = useBoolean();
    const { value: isTourFinishDailogOpen, setTrue: showTourFinishDailog, setFalse: hideTourFinishDailog } = useBoolean();

    const onNextClick = useCallback(() => {
        if (!tour) return;
        if (tour.currentStep + 1 < tourSteps.length) {
            updateTour(tour.currentStep + 1);
            return;
        }
        showTourFinishDailog();
        finishTour();
    }, [tour, finishTour, updateTour]);

    const onPrevClick = useCallback(() => {
        if (!tour) return;
        if (tour.currentStep > MAP_SPECIFIC_TOUR_START_INDEX) {
            updateTour(tour.currentStep - 1);
            return;
        }
    }, [tour, updateTour]);

    const handleCancel = useCallback(() => {
        if (!isTourFinished()) {
            showResumeDailog();
            return;
        }

        setIsMapTour(false);
    }, []);

    const handleResumeDailogExit = useCallback(() => {
        hideResumeDialog();
        setIsMapTour(false);
    }, []);

    const handleResumeDailogContinue = useCallback(() => {
        hideResumeDialog();
    }, []);

    const handleTourFinishClose = useCallback(() => {
        hideTourFinishDailog();
        setTour(undefined);
        setIsMapTour(false);
    }, []);

    if (!tour) {
        return null;
    }

    const step = tourSteps[tour.currentStep];
    const isLastStep = tour.currentStep + 1 === tourSteps.length;
    const showPrevButton = !isLastStep && tour.currentStep > MAP_SPECIFIC_TOUR_START_INDEX;
    const { title, content, primaryBtnLabel } = step as TourStep;

    const stepCounter = `Step ${tour.currentStep + 1} of ${tourSteps.length}`;
    return (
        <>
            <DialogBox
                open={isResumeDailogOpen}
                title={YOU_WANT_TO_EXIT}
                content={RESUME_TOUR_DESCRIPTION}
                cancelButtonLabel={EXIT}
                submitButtonLabel={RESUME_TOUR}
                showCancel
                onClose={hideResumeDialog}
                onCancel={handleResumeDailogExit}
                submit={handleResumeDailogContinue}
            />
            <DialogBox
                open={isTourFinishDailogOpen}
                title={YOU_ARE_ALL_SET}
                content={TOUR_FINISH_DESCRIPTION}
                submitButtonLabel={CONTINUE_EXPLORING}
                onClose={handleTourFinishClose}
                submit={handleTourFinishClose}
            />
            <TourDialogBox id="tour-dialogue">
                <DialogBoxContent>
                    <CloseButton data-testid="map-tour-close-icon" onClick={handleCancel} style={{ cursor: 'pointer' }} />
                    <TourStepper>{stepCounter}</TourStepper>
                    <TourHeader>{title}</TourHeader>
                    <TourContent>{content}</TourContent>
                </DialogBoxContent>
                <DialogBoxFooter>
                    {showPrevButton && (
                        <Button onClick={onPrevClick} fullWidth variant="outlined">
                            {PREVIOUS}
                        </Button>
                    )}
                    <Button onClick={onNextClick} fullWidth>
                        {primaryBtnLabel ? primaryBtnLabel : isLastStep ? DONE : NEXT}
                    </Button>
                </DialogBoxFooter>
            </TourDialogBox>
        </>
    );
};

MapTourDialogBox.displayName = 'MapTourDialogBox';
